import type React from "react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Alert, AlertDescription } from "../../components/ui/alert";
import {
  Loader2,
  AlertCircle,
  Award as IdCard,
  Key,
  Mail,
  CheckCircle2,
  Info,
  CircleX,
} from "lucide-react";
import { apiClient } from "../lib/api";
import { formatRut, validateRut, parseRut } from "../lib/rut-validator";
import { useAuthStore } from "../lib/stores/auth-store";
import { useConsentStore } from "@/lib/stores/consentimiento-store";
import { useBeneficiariosStore } from "../../lib/stores/beneficiarios-store";
import { usePolizasCacheStore } from "../lib/stores/polizas-cache-store";

export default function LoginPage() {
  const baseUrl = import.meta.env.VITE_BASE_URL;
  const navigate = useNavigate();
  const [step, setStep] = useState<"login" | "otp" | "consentimiento">("login");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>("");
  const [canRequestNewOtp, setCanRequestNewOtp] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [userEmail, setUserEmail] = useState<string>("");
  const [showEmailInput, setShowEmailInput] = useState(false);
  const [email, setEmail] = useState("");
  const setAuth = useAuthStore((state) => state.setAuth);
  const setConsentimientoStore = useConsentStore((state) => state.setConsent);
  const setHasUserSeenModal = usePolizasCacheStore(
    (state) => state.setHasUserSeenModal
  );

  // Login form
  const [rut, setRut] = useState("");
  const [numeroSerie, setNumeroSerie] = useState("");
  const [solicitudGuid, setSolicitudGuid] = useState("");

  // OTP form
  const [otp, setOtp] = useState("");

  const handleRutChange = (value: string) => {
    const formatted = formatRut(value);
    setRut(formatted);
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!validateRut(rut)) {
      setError("RUT inválido");
      return;
    }

    if (!numeroSerie.trim()) {
      setError("Número de serie requerido");
      return;
    }

    const parsed = parseRut(rut);
    if (!parsed) {
      setError("Error al procesar el RUT");
      return;
    }

    setLoading(true);

    if (showEmailInput && !email.trim()) {
      setError("Debes ingresar un email");
      return;
    }

    const response = await apiClient.startLogin({
      rut: parsed.rut,
      dv: parsed.dv,
      numeroSerie: numeroSerie.trim(),
      email: email.trim() || null,
    });

    setLoading(false);

    if (response.success && response.data) {
      setSolicitudGuid(response?.data?.guid);
      if (response.data?.email) {
        setUserEmail(response.data?.email);
      }
      setStep("otp");
    } else if (response.statusCode === 403) {
      setError("Ingresa tu email para continuar.");
      setShowEmailInput(true);
      setHasUserSeenModal(false);
    } else {
      setError("Error al iniciar sesión");
    }
  };

  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!otp.trim()) {
      setError("Código de verificación requerido");
      return;
    }

    setLoading(true);

    const response: any = await apiClient.verifyOtp({
      solicitudGuid,
      otp: otp.trim(),
    });

    setLoading(false);

    if (response.success && response.data) {
      setAuth(response.data, solicitudGuid);
      if (response.data?.email) {
        setUserEmail(response.data.email);
      }

      await new Promise((resolve) => setTimeout(resolve, 100));

      setStep("consentimiento");
      // navigate(`${baseUrl}/polizas`, { replace: true });
    } else {
      if (response.statusCode === 400) {
        setError(response.message || "Código de verificación inválido");
        setCanRequestNewOtp(false);
      } else if (response.statusCode === 409) {
        setError(
          response.message ||
            "Has superado el número máximo de intentos. Solicita un nuevo código de verificación."
        );
        setCanRequestNewOtp(true);
      } else {
        setError(response.message || "Error al verificar código");
        setCanRequestNewOtp(false);
      }
    }
  };

  const handleConsentimiento = async () => {
    setError("");

    const { clearCache } = usePolizasCacheStore.getState();
    const { clearBeneficiarios } = useBeneficiariosStore.getState();
    const { clearConsent } = useConsentStore.getState();

    clearCache();
    clearBeneficiarios();
    clearConsent();
    localStorage.setItem("beneficiarios", "");

    setConsentimientoStore(true);
    navigate(`${baseUrl}/polizas`, { replace: true });
  };

  const handleConsentimientoNoAcepto = async () => {
    setError("");

    const { clearCache } = usePolizasCacheStore.getState();
    const { clearBeneficiarios } = useBeneficiariosStore.getState();
    const { clearConsent } = useConsentStore.getState();

    clearCache();
    clearBeneficiarios();
    clearConsent();
    localStorage.setItem("beneficiarios", "");

    setConsentimientoStore(false);
    navigate(`${baseUrl}/polizas`, { replace: true });
  };

  const handleRequestNewOtp = async () => {
    setError("");
    setResendLoading(true);

    const response = await apiClient.requestNewOtp({
      SolicitudGuid: solicitudGuid,
      otp: otp.trim(),
    });

    setResendLoading(false);

    if (response.success) {
      setError("");
      setOtp("");
      setCanRequestNewOtp(false);
      setError(response.message || "OTP generado y enviado correctamente");
      setTimeout(() => setError(""), 3000);
    } else {
      setError(response.message || "Error al solicitar nuevo código");
    }
  };

  const handleResendCode = async () => {
    setError("");
    setOtp("");
    setCanRequestNewOtp(false);
    setStep("login");
  };

  return (
    <div className="flex min-h-screen flex-col">
      <div className="flex flex-1 items-center justify-center bg-gradient-to-br from-blue-50 via-white to-blue-50 px-4 py-12">
        <Card
          className={
            step === "consentimiento"
              ? "w-full   max-w-7xl px-8 border-t-4 border-t-[var(--color-z-blue)]"
              : "w-full max-w-md  shadow-xl border-t-4 border-t-[var(--color-z-blue)]"
          }
        >
          <CardHeader className="space-y-1 text-center pb-6">
            <div className="mx-auto mb-4 flex h-35 w-55 items-center justify-center">
              <img
                src="data:image/png;base64,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"
                alt="Zurich Seguros"
                className="h-full w-full object-contain"
              />
            </div>
            <CardTitle className="text-3xl font-bold text-[var(--color-z-blue)]">
              {step === "login"
                ? "Bienvenido"
                : step === "consentimiento"
                ? "Cláusula de Consentimiento"
                : "Verificación de Seguridad"}
            </CardTitle>
            <CardDescription className="text-base">
              {step === "login" ? (
                <>
                  <p>
                    Para comenzar con la designación de beneficiarios,
                    necesitamos verificar tu identidad. Ingresa tu RUT y número
                    de serie para continuar.
                  </p>
                  <p className="pt-2">
                    En caso de que no podamos validar la información de
                    inmediato,
                    <span className="text-[var(--color-z-blue)]">
                      {" "}
                      te pediremos un correo electrónico
                    </span>{" "}
                    para ayudarte a completar el proceso de forma segura.
                  </p>
                </>
              ) : step === "consentimiento" ? (
                "Protección de datos y términos de privacidad"
              ) : (
                "Protegemos tu información con verificación en dos pasos"
              )}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {error && (
              <Alert
                variant={
                  error.includes("correctamente") ? "default" : "destructive"
                }
                className="mb-4"
              >
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {step === "otp" && !error && (
              <Alert className="mb-4 border-blue-200 bg-blue-50">
                <Mail className="h-4 w-4 text-[var(--color-z-blue)]" />
                <AlertDescription className="text-sm">
                  <p className="font-semibold text-[var(--color-z-blue)] mb-1">
                    📧 Revisa tu correo electrónico
                  </p>
                  <p className="text-muted-foreground">
                    Hemos enviado un código de verificación a tu correo
                    registrado
                    {userEmail && (
                      <span className="font-medium text-[var(--color-z-blue)]">
                        {" "}
                        {userEmail}
                      </span>
                    )}
                    . Por favor, ingrésalo a continuación para continuar.
                  </p>
                </AlertDescription>
              </Alert>
            )}

            {step === "consentimiento" && !error && (
              <div className="p-8">
                {/* Introduction Box */}
                <div className="bg-[#EBF3F9] border-l-4 border-[#0066B3] rounded-lg p-6 mb-8">
                  <p className="text-[#1a3a52] font-semibold leading-relaxed">
                    Al seleccionar estas finalidades del presente documento,
                    aceptas que Zurich Chile y sus empresas relacionadas, usen
                    tu información personal en Chile y el extranjero.
                  </p>
                </div>

                {/* Detailed Information */}
                <div className="bg-gray-50 rounded-lg p-6 mb-6 border border-gray-200">
                  <p className="text-gray-700 leading-relaxed mb-4 text-pretty">
                    Esto aplica a la información que proporciona de cualquier
                    manera, incluyendo este documento u otros, negociaciones, o
                    servicios, ya sea directamente, por teléfono, en persona, en
                    línea, a través de apps, etc. Zurich Chile puede usar tu
                    información cuando lo permita la ley, para cumplir con
                    obligaciones legales o por otras razones legales.
                  </p>

                  <p className="text-gray-900 font-semibold mb-4">
                    Con tu permiso, Zurich Chile puede usar tus datos para:
                  </p>

                  {/* Benefits List */}
                  <ul className="space-y-3">
                    {[
                      "Ofrecerte productos o servicios.",
                      "Negociar, celebrar, o ejecutar contratos o servicios, incluyendo propuestas, cotizaciones o simulaciones.",
                      "Mantener tu información al día en todos tus contratos o servicios con Zurich Chile.",
                      "Corregir y actualizar tu información con fuentes de acceso público o la que tú proporciones.",
                      "Compartir tu información dentro del grupo Zurich Chile, incluso fuera de Chile, para fines administrativos, auditorías, comunicación comercial, entre otros.",
                      "Compartir tu información con proveedores de Zurich Chile que sigan sus instrucciones y mantengan la confidencialidad y seguridad.",
                      "Utilizar técnicas de análisis y algoritmos de aprendizaje para procesar grandes volúmenes de datos, crear perfiles de cliente, y evaluar productos o servicios que se ajusten mejor al cliente.",
                      "Analizar datos y elaborar estudios estadísticos sobre intereses del cliente para mejorar la calidad del servicio y la propuesta de valor.",
                    ].map((item, index) => (
                      <li
                        key={index}
                        className="flex gap-3 text-gray-700 leading-relaxed"
                      >
                        <span className="flex-shrink-0 w-2 h-2 bg-[#0066B3] rounded-full mt-2" />
                        <span className="text-pretty">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Warning Box */}
                <div className="bg-[var(--color-z-blue)]/10 border border-[var(--color-z-blue)]/10 rounded-lg p-5 flex gap-4">
                  <Info className="w-5 h-5 text-[var(--color-z-blue)] flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-gray-800 leading-relaxed">
                    <p className="text-pretty">
                      <span className="font-semibold">
                        Al dar tu consentimiento
                      </span>
                      , sigues teniendo derechos sobre tus datos, como acceder a
                      ellos, corregirlos, cancelarlos, oponerte a su uso, o
                      trasladarlos, en virtud de la ley o según corresponda.
                      Este consentimiento no anula ni modifica otros
                      consentimiento que hayas dado antes a Zurich Chile. Si hay
                      conflictos con permisos anteriores, este documento tendrá
                      prioridad legalmente. Para más información, visita nuestra
                      política de privacidad en{" "}
                      <a
                        href="https://www.zurich.cl/conocenos/politicas-de-privacidad"
                        className="text-[#0066B3] font-semibold hover:underline"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Políticas de privacidad Zurich Chile
                      </a>
                    </p>
                  </div>
                </div>
              </div>
            )}

            {step === "consentimiento" ? (
              <div className="space-y-5">
                <div className="relative"></div>
                <Button
                  onClick={handleConsentimiento}
                  className="w-full h-12 text-base font-semibold bg-[var(--color-z-blue)] hover:bg-[var(--color-z-blue)]/80 shadow-md transition-all"
                >
                  <>
                    <CheckCircle2 className="mr-2 h-5 w-5" />
                    Acepto los términos y condiciones
                  </>
                </Button>
                <Button
                  onClick={handleConsentimientoNoAcepto}
                  className="w-full h-12 text-base font-semibold bg-blue-50 text-neutral hover:bg-gray-200 shadow-md transition-all"
                >
                  <>
                    <CircleX className="mr-2 h-5 w-5" />
                    No acepto los términos y condiciones
                  </>
                </Button>
              </div>
            ) : step === "login" ? (
              <form onSubmit={handleLogin} className="space-y-5">
                <div className="space-y-2">
                  <Label
                    htmlFor="rut"
                    className="flex items-center gap-2 text-sm font-semibold"
                  >
                    <IdCard className="h-4 w-4 text-[var(--color-z-blue)]" />
                    RUT
                  </Label>
                  <div className="relative">
                    <div className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--color-z-blue)]">
                      <IdCard className="h-5 w-5" />
                    </div>
                    <Input
                      id="rut"
                      placeholder="12.345.678-9"
                      value={rut}
                      onChange={(e) => handleRutChange(e.target.value)}
                      disabled={loading}
                      maxLength={12}
                      className="pl-11 h-12 text-base border-2 focus:border-[var(--color-z-blue)] transition-colors"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label
                    htmlFor="numeroSerie"
                    className="flex items-center gap-2 text-sm font-semibold"
                  >
                    <Key className="h-4 w-4 text-[var(--color-z-blue)]" />
                    Número de Serie
                  </Label>
                  <div className="relative">
                    <div className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--color-z-blue)]">
                      <Key className="h-5 w-5" />
                    </div>
                    <Input
                      id="numeroSerie"
                      placeholder="Ingresa tu número de serie"
                      value={numeroSerie}
                      onChange={(e) => setNumeroSerie(e.target.value)}
                      disabled={loading}
                      className="pl-11 h-12 text-base border-2 focus:border-[var(--color-z-blue)] transition-colors"
                    />
                  </div>
                </div>
                {showEmailInput && (
                  <div className="space-y-2">
                    <Label
                      htmlFor="email"
                      className="flex items-center gap-2 text-sm font-semibold"
                    >
                      Email
                      <Mail className="h-4 w-4 text-[var(--color-z-blue)]" />
                    </Label>
                    <div className="relative">
                      <div className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--color-z-blue)]">
                        <Mail className="h-5 w-5" />
                      </div>
                      <Input
                        id="email"
                        type="email"
                        placeholder="ejemplo@correo.cl"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="pl-11 h-12 text-base border-2 focus:border-[var(--color-z-blue)] transition-colors"
                      />
                    </div>
                  </div>
                )}
                <Button
                  type="submit"
                  className="w-full h-12 text-base font-semibold bg-[var(--color-z-blue)] hover:bg-[var(--color-z-blue)]/80 shadow-md transition-all"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                      Verificando...
                    </>
                  ) : (
                    <>
                      <CheckCircle2 className="mr-2 h-5 w-5" />
                      Continuar
                    </>
                  )}
                </Button>
              </form>
            ) : (
              <form onSubmit={handleVerifyOtp} className="space-y-5">
                <div className="space-y-2">
                  <Label
                    htmlFor="otp"
                    className="flex items-center gap-2 text-sm font-semibold"
                  >
                    <Mail className="h-4 w-4 text-[var(--color-z-blue)]" />
                    Código de Verificación
                  </Label>
                  <div className="relative">
                    <div className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--color-z-blue)]">
                      <Mail className="h-5 w-5" />
                    </div>
                    <Input
                      id="otp"
                      placeholder="Ingresa el código de 6 dígitos"
                      value={otp}
                      onChange={(e) => setOtp(e.target.value)}
                      disabled={loading}
                      maxLength={6}
                      className="pl-11 h-12 text-base text-center tracking-widest font-semibold border-2 focus:border-[var(--color-z-blue)] transition-colors"
                    />
                  </div>
                </div>
                <Button
                  type="submit"
                  className="w-full h-12 text-base font-semibold bg-[var(--color-z-blue)] hover:bg-[var(--color-z-blue)]/80 shadow-md transition-all"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                      Verificando...
                    </>
                  ) : (
                    <>
                      <CheckCircle2 className="mr-2 h-5 w-5" />
                      Verificar Código
                    </>
                  )}
                </Button>
                {canRequestNewOtp ? (
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full h-11 border-2 border-[var(--color-z-blue)] text-[var(--color-z-blue)] hover:bg-blue-50 bg-transparent"
                    onClick={handleRequestNewOtp}
                    disabled={resendLoading}
                  >
                    {resendLoading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Enviando...
                      </>
                    ) : (
                      "Solicitar nuevo código"
                    )}
                  </Button>
                ) : (
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full h-11 border-2 hover:bg-gray-50 bg-transparent"
                    onClick={handleResendCode}
                    disabled={loading}
                  >
                    Volver al inicio
                  </Button>
                )}
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
